import { sendDataToServer, valueIsDefined } from "@legacy/core/utils/utils"
import { Component } from "react"
import deepcopy from "rfdc"

import InlineGeneralOverrideDisplay from "./components/InlineGeneralOverrideDisplay"
import InlineGeneralOverrideForm from "./forms/InlineGeneralOverrideForm"

const PAGE_MODES = {
    VIEW_ITEM: "VIEW_ITEM",
    EDIT_ITEM: "EDIT_ITEM",
}

const FORM_DATA_NAMES_BY_MODE = {
    EDIT_ITEM: "itemFormData",
}

// The form edits a discount, while the API stores the markup, which is its negative.
function toFormData(generalOverride) {
    return {
        ...generalOverride,
        discount_percent: -parseFloat(generalOverride.price_adjustment_percent),
    }
}

class InlineGeneralOverrideContainer extends Component {
    constructor(props) {
        super(props)

        let defaultMode = this.props.formMode || PAGE_MODES.VIEW_ITEM
        this.addToastToQueue = this.props.addToastToQueue

        const generalOverride = deepcopy()(this.props.generalOverride)
        const isNewAddition = generalOverride.__new__ === true

        if (isNewAddition) {
            defaultMode = PAGE_MODES.EDIT_ITEM
            delete generalOverride.__new__
            generalOverride.price_adjustment_percent = 0
        }

        this.state = {
            removed: false,
            isNewAddition: isNewAddition,
            itemData: generalOverride,
            itemFormData: toFormData(generalOverride),
            mode: defaultMode,
            errors: {
                overrideErrors: {},
            },
        }
    }

    // The markup's floor of -100 is the discount's ceiling of 100, so the API's message is restated in discount terms.
    setErrors = (fieldName, message, errorDict) => {
        if (fieldName === "price_adjustment_percent") {
            errorDict.discount_percent =
                message === "Ensure this value is greater than or equal to -100."
                    ? "Ensure this value is less than or equal to 100."
                    : message
        }
        else {
            errorDict[fieldName] = message
        }
    }

    dataManipulator = (data, state) => {
        return {
            external_client: this.props.client.id,
            pricebook_item_type: this.props.pricebookItemType,
            price_adjustment_percent: -parseFloat(data.discount_percent),
        }
    }

    addOverride = async () => {
        const dataName = "itemFormData"
        const submittingName = "submitting"
        const errorDictName = "overrideErrors"

        const endpoint = DjangoUrls["pricebook:api-pricebookitem-client-general-overrides-list"](window.MARKETPLACE_ENTITY_SLUG)
        const endpointMethod = "POST"

        const onSuccess = (createdOverride) => {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.itemData = createdOverride
                updatedState.itemFormData = toFormData(createdOverride)
                updatedState.isNewAddition = false
                return updatedState
            })

            this.props.updateOverride(createdOverride)

            this.switchFormMode(PAGE_MODES.VIEW_ITEM)

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "General override created",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "General override could not be created",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, this.dataManipulator, this.setErrors)
    }

    updateOverride = async () => {
        const dataName = "itemFormData"
        const submittingName = "submitting"
        const errorDictName = "overrideErrors"

        const endpoint = DjangoUrls["pricebook:api-pricebookitem-client-general-overrides-detail"](
            window.MARKETPLACE_ENTITY_SLUG,
            this.state.itemData.id,
        )
        const endpointMethod = "PUT"

        const onSuccess = (updatedOverride) => {
            this.setState((state, props) => {
                let updatedState = state
                updatedState.itemData = updatedOverride
                updatedState.itemFormData = toFormData(updatedOverride)
                return updatedState
            })

            this.props.updateOverride(updatedOverride)

            this.switchFormMode(PAGE_MODES.VIEW_ITEM)

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "General override updated",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "General override could not be updated",
            })
        }

        await sendDataToServer(this, endpoint, endpointMethod, dataName, submittingName, errorDictName, onSuccess, onError, this.dataManipulator, this.setErrors)
    }

    deleteOverride = async () => {
        const errorDictName = "overrideErrors"

        const endpoint = DjangoUrls["pricebook:api-pricebookitem-client-general-overrides-detail"](
            window.MARKETPLACE_ENTITY_SLUG,
            this.state.itemData.id,
        )

        const onSuccess = () => {
            this.softDeleteItem()
            this.props.deleteOverride()

            this.addToastToQueue({
                type: "success",
                variant: "default",
                title: "General override deleted",
            })
        }

        const onError = () => {
            this.addToastToQueue({
                type: "error",
                variant: "default",
                title: "General override could not be deleted",
            })
        }

        await sendDataToServer(this, endpoint, "DELETE", null, null, errorDictName, onSuccess, onError)
    }

    updateFormData = (formName, fieldName, fieldValue) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState[formName][fieldName] = fieldValue
            return updatedState
        })
    }

    switchFormMode = (mode) => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.mode = mode
            return updatedState
        })
    }

    softDeleteItem = () => {
        this.setState((state, props) => {
            let updatedState = state
            updatedState.removed = true
            return updatedState
        })
    }

    validateOverride = (override) => {
        let isValid = true
        let errors = {}

        if (!valueIsDefined(override.discount_percent) || override.discount_percent === "") {
            isValid = false
            errors.discount_percent = "Discount cannot be blank."
        }

        return { isValid, errors }
    }

    handleOverride = (callback) => {
        const { isValid, errors } = this.validateOverride(this.state.itemFormData)

        if (isValid) {
            this.setState((state) => {
                let updatedState = { ...state }
                delete updatedState.itemFormData.errors
                callback()

                return updatedState
            })
        }
        else {
            this.setState((state) => {
                let updatedState = { ...state }
                updatedState.itemFormData.errors = errors
                return updatedState
            })
        }
    }

    // Handle Actions

    handleActionRequest = (action) => {
        switch (action) {
            case "ITEM_EDIT":
                this.switchFormMode(PAGE_MODES.EDIT_ITEM)
                break
            case "ITEM_ADD":
                this.handleOverride(() => this.addOverride())
                break
            case "ITEM_SAVE":
                this.handleOverride(() => this.updateOverride())
                break
            case "ITEM_DELETE":
                this.deleteOverride()
                break
            case "ITEM_CANCEL_ADD":
                this.softDeleteItem()
                this.props.deleteOverride()
                break
            case "ITEM_CANCEL_EDITS":
                this.setState((state, props) => {
                    let updatedState = state
                    updatedState.itemFormData = toFormData(state.itemData)
                    return updatedState
                })
                this.switchFormMode(PAGE_MODES.VIEW_ITEM)
                break

            default:
                console.error(`No action handler exists for action "${action}".`)
        }
    }

    // Render

    render = () => {
        if (!this.state.removed) {
            if (this.state.mode === PAGE_MODES.VIEW_ITEM) {
                return (
                    <InlineGeneralOverrideDisplay
                        item={this.state.itemData}
                        requestAction={this.handleActionRequest}
                    />
                )
            }
            else if (this.state.mode === PAGE_MODES.EDIT_ITEM) {
                return (
                    <InlineGeneralOverrideForm
                        item={this.state.itemFormData}
                        isNewAddition={this.state.isNewAddition}
                        onFormDataChange={(fieldName, fieldValue) =>
                            this.updateFormData(FORM_DATA_NAMES_BY_MODE[this.state.mode], fieldName, fieldValue)
                        }
                        requestAction={this.handleActionRequest}
                        errors={this.state.errors.overrideErrors}
                    />
                )
            }
            else {
                return (
                    <div className="data-panel-container data-panel-container--with-margin">
                        <div className="data-panel" aria-label="Unknown Form Mode">
                            <div className="data-panel__form">
                                <p className="data-panel__form__caption">An unhandled form mode was supplied.</p>
                            </div>
                        </div>
                    </div>
                )
            }
        }
        else {
            return null
        }
    }
}

export default InlineGeneralOverrideContainer
